import {
    compactNumber,
    formatCurrency,
    formatDurationMilliseconds,
    humanFriendlyCurrency,
    humanFriendlyNumber,
    percentage,
    significantDecimalPlaces,
} from './format'

export type YAxisFormat =
    | 'numeric'
    | 'percentage'
    | 'percentage_scaled'
    | 'currency'
    | 'duration'
    | 'duration_ms'
    | 'duration_ns'
    | 'short'

export interface YFormatterConfig {
    /** `percentage` expects values in `0–100`; `percentage_scaled` expects values in `0–1`. */
    format?: YAxisFormat
    prefix?: string
    suffix?: string
    decimalPlaces?: number
    minDecimalPlaces?: number
    /** Currency code (e.g. `'USD'`). Used when `format === 'currency'`. */
    currency?: string
}

function formatNanoseconds(value: number): string {
    if (value < 0) {
        return `-${formatNanoseconds(-value)}`
    }
    const absoluteValue = Math.abs(value)
    if (absoluteValue < 1_000) {
        return `${humanFriendlyNumber(value)}ns`
    }
    if (absoluteValue < 1_000_000) {
        return `${humanFriendlyNumber(value / 1_000)}µs`
    }
    return formatDurationMilliseconds(value / 1_000_000)
}

export function buildYTickFormatter(config: YFormatterConfig): (value: number) => string {
    const { format, prefix, suffix, decimalPlaces, minDecimalPlaces, currency } = config
    return (value: number): string => {
        let formatted = humanFriendlyNumber(
            value,
            decimalPlaces ?? significantDecimalPlaces(value, minDecimalPlaces),
            minDecimalPlaces
        )
        switch (format) {
            case 'duration':
                formatted = formatDurationMilliseconds(value * 1000)
                break
            case 'duration_ms':
                formatted = formatDurationMilliseconds(value)
                break
            case 'duration_ns':
                formatted = formatNanoseconds(value)
                break
            case 'percentage':
                formatted = percentage(value / 100, decimalPlaces ?? significantDecimalPlaces(value, minDecimalPlaces))
                break
            case 'percentage_scaled':
                formatted = percentage(value, decimalPlaces ?? significantDecimalPlaces(value * 100, minDecimalPlaces))
                break
            case 'currency':
                try {
                    formatted = currency ? formatCurrency(value, currency) : humanFriendlyCurrency(value)
                } catch {
                    formatted = humanFriendlyCurrency(value)
                }
                break
            case 'short':
                formatted = compactNumber(value)
                break
            case 'numeric':
            case undefined:
            default:
                break
        }
        return `${prefix ?? ''}${formatted}${suffix ?? ''}`
    }
}
